import type { DimMode } from "./imageDims";

export interface ImageSpecEntry {
  id: string;
  label: string;
  hint: string;
  mode: DimMode;
  spec: {
    width?: number;
    height?: number;
    minWidth?: number;
    minHeight?: number;
    minRatio?: number;
    maxRatio?: number;
    aspectRatio?: string;
  };
}

export const IMAGE_SPECS: Record<string, ImageSpecEntry> = {
  bannerHero: {
    id: "bannerHero",
    label: "Banner Image (Hero)",
    hint: "Recommended size: 1267 x 400 px (Hero banner, ratio ~3.17:1)",
    mode: "min",
    spec: { minWidth: 1200, minHeight: 350, minRatio: 3.0, maxRatio: 3.4, aspectRatio: "1267 x 400 px" },
  },
  bannerPromo: {
    id: "bannerPromo",
    label: "Banner Image (Promotional)",
    hint: "Recommended size: 350 x 120 px (Promotional banner, ratio ~2.9:1)",
    mode: "min",
    spec: { minWidth: 300, minHeight: 100, minRatio: 2.8, maxRatio: 3.2, aspectRatio: "350 x 120 px" },
  },
  bannerSidebar: {
    id: "bannerSidebar",
    label: "Banner Image (Sidebar)",
    hint: "Recommended size: 300 x 400 px (Sidebar banner, portrait)",
    mode: "min",
    spec: { minWidth: 250, minHeight: 350, minRatio: 0.7, maxRatio: 0.9, aspectRatio: "300 x 400 px" },
  },
  bannerHeroPoster: {
    id: "bannerHeroPoster",
    label: "Poster Image",
    hint: "Recommended size: 300 x 450 px (Movie poster - 2:3 ratio)",
    mode: "portrait",
    spec: { width: 300, height: 450, aspectRatio: "300 x 450 px" },
  },
  moviePoster: {
    id: "moviePoster",
    label: "Movie Poster",
    hint: "Recommended size: 300 x 450 px (Movie poster - 2:3 ratio)",
    mode: "portrait",
    spec: { width: 300, height: 450, aspectRatio: "300 x 450 px" },
  },
  offerListing: {
    id: "offerListing",
    label: "Offer Listing Image",
    hint: "Recommended size: 800 x 360 px (Offer card thumbnail, ~2.2:1)",
    mode: "range",
    spec: { minRatio: 2.0, maxRatio: 2.4, aspectRatio: "800 x 360 px" },
  },
  offerDetail: {
    id: "offerDetail",
    label: "Offer Detail Image",
    hint: "Recommended size: 1200 x 500 px (Offer detail banner)",
    mode: "range",
    spec: { minRatio: 2.2, maxRatio: 2.6, aspectRatio: "1200 x 500 px" },
  },
  mamangamCard: {
    id: "mamangamCard",
    label: "Card Image",
    hint: "Recommended size: 252 x 446 px (Vertical, ~0.56:1)",
    mode: "exact",
    spec: { width: 252, height: 446, aspectRatio: "252 x 446 px" },
  },
  mamangamDetail: {
    id: "mamangamDetail",
    label: "Detail Image",
    hint: "Recommended size: 916 x 297 px (Horizontal, ~3.08:1)",
    mode: "exact",
    spec: { width: 916, height: 297, aspectRatio: "916 x 297 px" },
  },
  aboutIntroSlider: {
    id: "aboutIntroSlider",
    label: "Intro Slider Image",
    hint: "Recommended size: 572 x 760 px (Portrait, 3:4 ratio)",
    mode: "range",
    spec: { minRatio: 0.7, maxRatio: 0.8, aspectRatio: "572 x 760 px" },
  },
  aboutStatsBackground: {
    id: "aboutStatsBackground",
    label: "Stats Background",
    hint: "Recommended size: 1664 x 600 px (Wide landscape banner)",
    mode: "range",
    spec: { minRatio: 2.6, maxRatio: 2.9, aspectRatio: "1664 x 600 px" },
  },
  aboutStatsLeftImage: {
    id: "aboutStatsLeftImage",
    label: "Stats Left Image",
    hint: "Recommended size: 800 x 600 px (4:3 landscape)",
    mode: "range",
    spec: { minRatio: 1.2, maxRatio: 1.5, aspectRatio: "800 x 600 px" },
  },
  aboutWhatWeDoTile: {
    id: "aboutWhatWeDoTile",
    label: "What We Do Tile Background",
    hint: "Recommended size: 1664 x 600 px (first card, col-lg-8) / 808 x 600 px (rest, col-lg-4)",
    mode: "range",
    spec: { minRatio: 1.2, maxRatio: 2.9, aspectRatio: "1664 x 600 px or 808 x 600 px" },
  },
  aboutVision: {
    id: "aboutVision",
    label: "Vision Card Background",
    hint: "Recommended size: 780 x 230 px (Wide thin banner)",
    mode: "range",
    spec: { minRatio: 3.2, maxRatio: 3.6, aspectRatio: "780 x 230 px" },
  },
  aboutMission: {
    id: "aboutMission",
    label: "Mission Card Background",
    hint: "Recommended size: 780 x 230 px (Wide thin banner)",
    mode: "range",
    spec: { minRatio: 3.2, maxRatio: 3.6, aspectRatio: "780 x 230 px" },
  },
  advertiseTabImage: {
    id: "advertiseTabImage",
    label: "Advertise Tab Image",
    hint: "Recommended size: 800 x 420 px (Tab accordion card, ~1.9:1)",
    mode: "range",
    spec: { minRatio: 1.7, maxRatio: 2.1, aspectRatio: "800 x 420 px" },
  },
  advertiseOffscreenCard: {
    id: "advertiseOffscreenCard",
    label: "Offscreen Card Image",
    hint: "Recommended size: 800 x 420 px (Offscreen card, ~1.9:1)",
    mode: "range",
    spec: { minRatio: 1.7, maxRatio: 2.1, aspectRatio: "800 x 420 px" },
  },
  advertiseDetailsBackground: {
    id: "advertiseDetailsBackground",
    label: "Advertising Details Background",
    hint: "Recommended size: 1440 x 521 px (Wide banner)",
    mode: "exact",
    spec: { width: 1440, height: 521, aspectRatio: "1440 x 521 px" },
  },
  advertiseDetailsQr: {
    id: "advertiseDetailsQr",
    label: "Advertising Details QR",
    hint: "Recommended size: 176 x 176 px (Square)",
    mode: "exact",
    spec: { width: 176, height: 176, aspectRatio: "176 x 176 px" },
  },
  specialScreeningTile: {
    id: "specialScreeningTile",
    label: "Special Screening Tile Background",
    hint: "Recommended size: 1664 x 600 px (first) / 808 x 600 px (rest)",
    mode: "range",
    spec: { minRatio: 1.2, maxRatio: 2.9, aspectRatio: "1664 x 600 px or 808 x 600 px" },
  },
  specialScreeningFullBanner: {
    id: "specialScreeningFullBanner",
    label: "Special Screening Full Banner",
    hint: "Recommended size: 1440 x 615 px (Full-width banner)",
    mode: "range",
    spec: { minRatio: 2.2, maxRatio: 2.5, aspectRatio: "1440 x 615 px" },
  },
  specialScreeningPremiumBg: {
    id: "specialScreeningPremiumBg",
    label: "Premium Package Background",
    hint: "Recommended size: 1890 x 222 px (Wide thin gradient)",
    mode: "range",
    spec: { minRatio: 8.0, maxRatio: 9.0, aspectRatio: "1890 x 222 px" },
  },
  specialScreeningSideImage: {
    id: "specialScreeningSideImage",
    label: "Bulk Booking Side Image",
    hint: "Recommended size: 618 x 412 px (3:2 landscape)",
    mode: "range",
    spec: { minRatio: 1.4, maxRatio: 1.6, aspectRatio: "618 x 412 px" },
  },
};

export function getImageSpec(id: string): ImageSpecEntry | undefined {
  return IMAGE_SPECS[id];
}
